import React, { Component, Fragment } from 'react';
import { translate } from 'react-i18next';
import { Table, Tooltip, OverlayTrigger, Button } from 'react-bootstrap';
import Moment from 'moment';
import Dialog from 'react-bootstrap-dialog';
import config from '../config';
import { Link } from 'react-router-dom';
import TooltipButton from './TooltipButton';
import TooltipLink from './TooltipLink';
import {
    canReceiveFirmwareUpdate,
    emphasised,
    expectedUpdateBy,
    mark
} from '../firmwareUpdate';
import axios from 'axios';
Moment.locale('en');

class PlayerTable extends Component {
    // five clicks on the Firmware heading reveals the custom index field
    state = { advancedFirmware: false, firmwareHeadingClicks: 0 };

    revealAdvancedFirmware = () => {
        if (
            !this.props.userIsAuthorized(this.props.user, ['staff_super_admin'])
        ) {
            return;
        }
        this.setState(prev => {
            const clicks = prev.firmwareHeadingClicks + 1;
            if (clicks < 5) return { firmwareHeadingClicks: clicks };
            return {
                firmwareHeadingClicks: 0,
                advancedFirmware: !prev.advancedFirmware
            };
        });
    };

    copyEmailToClipboard = (email) => {
        navigator.clipboard.writeText(email).catch(err => {
            console.error('Failed to copy email:', err);
        });
    };

    didConfirmDelete(player) {
        this.props.onDelete(player);
    }

    cancelFirmwareUpdate = (player, authorisationId) => {
        axios
            .delete(
                `${config.baseURL}/players/${player.uuid}/firmware-updates/${authorisationId}`
            )
            .then(() => {
                this.dialog.hide();
                if (this.props.onFirmwareChanged) this.props.onFirmwareChanged();
            })
            .catch(error => {
                console.log(error);
            });
    };

    showFirmwareHistoryModal = player => {
        const { t } = this.props;

        axios
            .get(`${config.baseURL}/players/${player.uuid}/firmware-updates`)
            .then(response => {
                const updates = response.data.firmwareUpdates || [];

                this.dialog.show({
                    title: t('PlayersTable.firmwarehistory'),
                    body: updates.length ? (
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <Table striped condensed>
                                <thead>
                                    <tr>
                                        <th>{t('PlayersTable.firmware_version')}</th>
                                        <th>{t('PlayersTable.firmware_status')}</th>
                                        <th>{t('PlayersTable.firmware_authorised_by')}</th>
                                        <th>{t('PlayersTable.firmware_when')}</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {updates.map(update => (
                                        <tr key={update.id}>
                                            <td>{update.targetVersion}</td>
                                            <td>
                                                {update.status}
                                                {update.attempts > 0
                                                    ? ` (${update.attempts})`
                                                    : ''}
                                            </td>
                                            <td>
                                                {update.issuedBy
                                                    ? update.issuedBy.name ||
                                                      update.issuedBy.email
                                                    : '-'}
                                                {update.reason ? (
                                                    <div className="text-muted">
                                                        <small>{update.reason}</small>
                                                    </div>
                                                ) : null}
                                            </td>
                                            <td>
                                                {Moment(update.issuedAt).fromNow()}
                                            </td>
                                            <td>
                                                {update.status === 'pending' ? (
                                                    <Button
                                                        bsSize="xsmall"
                                                        className="btn-danger"
                                                        onClick={() =>
                                                            this.cancelFirmwareUpdate(player, update.id)
                                                        }
                                                    >
                                                        {t('PlayersTable.firmware_cancel')}
                                                    </Button>
                                                ) : null}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        t('PlayersTable.firmwarehistoryempty')
                    ),
                    actions: [
                        Dialog.Action(
                            t('Dialog.close'),
                            () => this.dialog.hide(),
                            'btn-default'
                        )
                    ],
                    bsSize: 'large'
                });
            })
            .catch(error => {
                console.log(error);
            });
    };

    handleUpdateFirmwareClicked(player) {
        const { t } = this.props;

        // ask what the update server is offering before asking anyone to approve it
        axios
            .get(`${config.baseURL}/players/firmware-release`)
            .then(response => this.confirmFirmwareUpdate(player, response.data.version))
            .catch(() => {
                this.dialog.showAlert(t('PlayersTable.firmwarereleaseunavailable'));
            });
    }

    confirmFirmwareUpdate(player, targetVersion) {
        const { t } = this.props;

        // the dialog body is a snapshot, so read the input on confirm
        this.customIndexInput = null;

        this.dialog.show({
            title: t('PlayersTable.updatefirmware'),
            body: (
                <Fragment>
                    <div>
                        {emphasised(
                            t('PlayersTable.updatefirmwareconfirm', {
                                name: player.name,
                                version: mark(player.reportedVersion),
                                target: mark(targetVersion)
                            })
                        )}
                    </div>
                    {this.state.advancedFirmware ? (
                        <div style={{ marginTop: 14 }}>
                            <label
                                htmlFor="customIndexUrl"
                                style={{ fontWeight: 'normal' }}
                            >
                                {t('PlayersTable.customindexlabel')}
                            </label>
                            <input
                                id="customIndexUrl"
                                type="url"
                                className="form-control"
                                placeholder="https://.../latest.json"
                                ref={el => (this.customIndexInput = el)}
                            />
                        </div>
                    ) : null}
                </Fragment>
            ),
            prompt: Dialog.TextPrompt({
                placeholder: t('PlayersTable.updatefirmwarereason')
            }),
            actions: [
                Dialog.Action(
                    t('Dialog.cancel'),
                    () => {},
                    'btn-default'
                ),
                Dialog.Action(
                    t('PlayersTable.updatefirmware'),
                    dialog =>
                        this.props
                            .onUpdateFirmware(
                                player,
                                dialog.value,
                                this.customIndexInput
                                    ? this.customIndexInput.value.trim()
                                    : ''
                            )
                            .then(result => {
                                // a 200 can still mean skipped, not authorised
                                const outcome =
                                    result &&
                                    result.results &&
                                    result.results[0];
                                if (outcome && outcome.status !== 'authorised') {
                                    this.dialog.showAlert(
                                        emphasised(
                                            t('PlayersTable.updatefirmwareskipped', {
                                                name: player.name,
                                                target: mark(
                                                    (result && result.version) ||
                                                        targetVersion
                                                ),
                                                reason: outcome.detail || ''
                                            })
                                        )
                                    );
                                    return;
                                }

                                const due = expectedUpdateBy(player);
                                this.dialog.showAlert(
                                    emphasised(
                                        t(
                                            due
                                                ? 'PlayersTable.updatefirmwarequeuedby'
                                                : 'PlayersTable.updatefirmwarequeued',
                                            {
                                                name: player.name,
                                                target: mark(
                                                    (result && result.version) ||
                                                        targetVersion
                                                ),
                                                time: due
                                                    ? Moment.duration(
                                                          due.getTime() - Date.now()
                                                      ).humanize()
                                                    : ''
                                            }
                                        )
                                    )
                                );
                            })
                            .catch(error =>
                                this.dialog.showAlert(
                                    t('PlayersTable.updatefirmwarefailed', {
                                        name: player.name,
                                        reason:
                                            (error.response &&
                                                error.response.data &&
                                                error.response.data.message) ||
                                            error.message ||
                                            ''
                                    })
                                )
                            ),
                    'btn-danger'
                )
            ],
            bsSize: 'large'
        });
    }

    handleDeleteClicked(player) {
        const { t } = this.props;

        this.dialog.show({
            title: t('Dialog.warning'),
            body: t('Dialog.sure_deletion'),
            actions: [
                Dialog.Action(
                    t('Dialog.cancel'),
                    () => console.log('Cancel!'),
                    'btn-default'
                ),
                Dialog.Action(
                    t('Dialog.delete'),
                    () => this.didConfirmDelete(player),
                    'btn-danger'
                )
            ],
            bsSize: 'large',
            onHide: dialog => {
                dialog.hide();
            }
        });
    }

    showNotesModal = (notes) => {
        const { t } = this.props;
        this.dialog.show({
            title: t('OrganisationPlayersTable.notes'),
            body: (
                <div style={{ whiteSpace: 'pre-wrap', maxHeight: '400px', overflowY: 'auto' }}>
                    {notes}
                </div>
            ),
            actions: [
                Dialog.Action(
                    t('Dialog.close'),
                    () => this.dialog.hide(),
                    'btn-default'
                )
            ],
            bsSize: 'large'
        });
    };


    render() {
        const { t } = this.props;
        let playerRows = this.props.players.map((player, index) => {
            return (
                <tr key={index}>
                    <td align="center" width={80}>
                        <OverlayTrigger
                            placement="top"
                            delayShow="300"
                            delayHide="150"
                            overlay={
                                <Tooltip id="tooltip">
                                    <strong>
                                        {t('PlayersTable.previewplayer')}
                                    </strong>
                                </Tooltip>
                            }
                        >
                            <a
                                id="previewButton"
                                className="btn btn-primary"
                                target="_blank"
                                href={`${
                                    config.playersBaseURL
                                }/mediaserver/unit.php?playerUUID=${
                                    player.uuid
                                }&preview=1`}
                            >
                                <em className="fa fa-eye" />
                            </a>
                        </OverlayTrigger>
                    </td>
                    <td className="hidden-xs hidden-sm">
                        {player.online ? (
                            <span className="label label-success">
                                {t('PlayersTable.online')}
                            </span>
                        ) : (
                            <span className="label label-danger">
                                {t('PlayersTable.offline')}
                            </span>
                        )}
                    </td>
                    <td>
                        <Link
                           style={{
                            alignItems: "center",
                            textAlign: "left"
                          }}
                         className="label label-primary"
                            to={`/organisations/${
                                player.organisationUUID
                            }/players/${player.uuid}`}
                        >
                            {player.name}
                        </Link>
                    </td>
                    <td className="hidden-xs hidden-sm">{player.location}</td>
                    <td className="hidden-xs hidden-sm hidden-md">
                        {player.macAddress}
                    </td>
                    <td className="hidden-xs hidden-sm">
                        {player.serialNumber}
                    </td>
                    <td className="hidden-xs hidden-sm">
                        {player.notes ? (
                            player.notes.length > 100 ? (
                                <span
                                    className="label label-info"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => this.showNotesModal(player.notes)}
                                >
                                    <em className="fa fa-sticky-note" /> Notes
                                </span>
                            ) : (
                                <OverlayTrigger
                                    placement="top"
                                    delayShow="300"
                                    delayHide="150"
                                    overlay={
                                        <Tooltip id={`notes-tooltip-${index}`}>
                                            {player.notes}
                                        </Tooltip>
                                    }
                                >
                                    <span className="label label-info" style={{ cursor: 'pointer' }}>
                                        <em className="fa fa-sticky-note" /> Notes
                                    </span>
                                </OverlayTrigger>
                            )
                        ) : (
                            <span className="text-muted">—</span>
                        )}
                    </td>
                    <td className="hidden-xs hidden-sm">
                        {player.email ? (
                            <OverlayTrigger
                                placement="top"
                                delayShow="300"
                                delayHide="150"
                                overlay={
                                    <Tooltip id={`email-tooltip-${index}`}>
                                        {player.email} (click to copy)
                                    </Tooltip>
                                }
                            >
                                <span
                                    className="label label-success"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => this.copyEmailToClipboard(player.email)}
                                >
                                    <em className="fa fa-envelope" />
                                </span>
                            </OverlayTrigger>
                        ) : (
                            <span className="text-muted">—</span>
                        )}
                    </td>
                    <td className="hidden-sm hidden-xs hidden-md">
                        {Moment(player.installDate).format('YYYY-MM-DD HH:mm')}
                    </td>
                    <td className="hidden-xs">
                        {player.lastSeen
                            ? Moment(player.lastSeen).fromNow()
                            : t('PlayersTable.never')}
                    </td>
                    <td className="hidden-xs hidden-sm">
                        {player.reportedVersion || (
                            <span className="text-muted">-</span>
                        )}
                        {player.pendingUpdateVersion ? (
                            <div>
                                <span className="label label-warning">
                                    <em className="fas fa-hourglass-half" />{' '}
                                    {player.pendingUpdateVersion}
                                </span>
                            </div>
                        ) : player.lastUpdateStatus === 'failed' ? (
                            <div>
                                <span className="label label-danger">
                                    {t('PlayersTable.firmware_failed')}
                                </span>
                            </div>
                        ) : null}
                    </td>
                    <td align="center" width={260} style={{ whiteSpace: 'nowrap' }}>
                        <div className="btn-group">
                        {this.props.userIsAuthorized(this.props.user, [
                            'staff_super_admin'
                        ]) ? (
                            <Fragment>
                                <TooltipLink
                                    id="editButton"
                                    className="btn btn-default"
                                    tooltip={t('PlayersTable.editplayer')}
                                    to={`/organisations/${
                                        player.organisationUUID
                                    }/players/${player.uuid}/edit?rel=admin`}
                                >
                                    <em className="fas fa-pencil-alt" />
                                </TooltipLink>
                                {player.reportedVersion ? (
                                    <TooltipButton
                                        id="firmwareHistoryButton"
                                        className="btn-default"
                                        tooltip={t('PlayersTable.firmwarehistory')}
                                        onClick={() =>
                                            this.showFirmwareHistoryModal(player)
                                        }
                                    >
                                        <em className="fas fa-history" />
                                    </TooltipButton>
                                ) : null}
                                {canReceiveFirmwareUpdate(player) ? (
                                    <TooltipButton
                                        id="updateFirmwareButton"
                                        className="btn-default"
                                        tooltip={t('PlayersTable.updatefirmware')}
                                        onClick={() =>
                                            this.handleUpdateFirmwareClicked(
                                                player
                                            )
                                        }
                                    >
                                        <em className="fas fa-download" />
                                    </TooltipButton>
                                ) : null}
                                <TooltipButton
                                    id="deleteButton"
                                    className="btn-danger"
                                    tooltip={t('PlayersTable.editplayer')}
                                    onClick={() =>
                                        this.handleDeleteClicked(player)
                                    }
                                >
                                    <em className="fa fa-trash" />
                                </TooltipButton>
                            </Fragment>
                        ) : null}
                        </div>
                    </td>
                </tr>
            );
        });

        return (
            <div id="players" className="playerTable">
                <Dialog
                    ref={el => {
                        this.dialog = el;
                    }}
                />
                <Table striped bordered condensed hover>
                    <thead>
                        <tr>
                            <th>{t('PlayersTable.preview')}</th>
                            <th className="hidden-xs hidden-sm">
                                {t('PlayersTable.status')}
                            </th>
                            <th>{t('PlayersTable.name')}</th>
                            <th className="hidden-xs hidden-sm">
                                {t('PlayersTable.location')}
                            </th>
                            <th className="hidden-xs hidden-sm hidden-md">
                                {t('PlayersTable.mac_address')}
                            </th>
                            <th className="hidden-xs hidden-sm">
                                {t('PlayersTable.serial_number')}
                            </th>
                            <th className="hidden-xs hidden-sm">
                                {t('PlayersTable.notes')}
                            </th>
                            <th className="hidden-xs hidden-sm" width="60">
                                <img src={require('../email.png')} alt="Email" style={{ width: '24px', height: '24px' }} />
                            </th>
                            <th className="hidden-xs hidden-sm hidden-md">
                                {t('PlayersTable.install_date')}
                            </th>
                            <th className="hidden-xs">
                                {t('PlayersTable.last_seen')}
                            </th>
                            <th
                                className="hidden-xs hidden-sm"
                                onClick={this.revealAdvancedFirmware}
                                style={{ userSelect: 'none' }}
                            >
                                {t('PlayersTable.firmware')}
                                {this.state.advancedFirmware ? (
                                    <em
                                        className="fas fa-flask"
                                        style={{ marginLeft: 6 }}
                                        title={t('PlayersTable.customindexon')}
                                    />
                                ) : null}
                            </th>
                            <th>
                                <em className="fa fa-cog" />
                            </th>
                        </tr>
                    </thead>
                    <tbody>{playerRows}</tbody>
                </Table>
            </div>
        );
    }
}

export default translate('translations')(PlayerTable);
